import { useEffect, useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Gift, Copy, Check, Users, DollarSign, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * "Refer & Earn" tile shown on the Host Dashboard.
 * Displays the host's unique referral URL, signup count, certified-referral
 * count, and estimated $25/certified credit balance.
 */
export default function HostReferralTile() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referrals/mine").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const copyLink = async () => {
    if (!data?.referral_url) return;
    try {
      await navigator.clipboard.writeText(data.referral_url);
      setCopied(true);
      toast.success("Referral link copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — long-press or select the link to copy manually");
    }
  };

  if (!data) return null;

  return (
    <div
      className="bg-white rounded-3xl border border-slate-200 p-6 md:p-8 relative overflow-hidden"
      data-testid="host-referral-tile"
    >
      {/* Decorative gradient stripe */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-emerald-600 to-[#D4AF37]" />

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase text-emerald-700 font-semibold">
            <Gift className="w-3 h-3" /> Refer & Earn
          </div>
          <h3 className="mt-2 font-serif text-2xl md:text-3xl text-[#0A1128] tracking-tight">
            $25 credit for every certified host you refer.
          </h3>
          <p className="mt-2 text-sm text-slate-600 max-w-lg">
            Share your unique link with other short-term-rental hosts. When they sign up and get certified,
            you get <b>$25 in credit</b> — <span className="text-emerald-700 font-semibold">applied automatically at your next Stripe checkout</span>. No admin required.
          </p>
        </div>
        <div className="w-11 h-11 rounded-2xl bg-emerald-50 flex items-center justify-center shrink-0">
          <Gift className="w-5 h-5 text-emerald-600" />
        </div>
      </div>

      {/* Copy link */}
      <div className="mt-6 flex items-stretch gap-2 flex-wrap">
        <div className="flex-1 min-w-0 flex items-center gap-2 px-4 h-11 rounded-full bg-slate-50 border border-slate-200">
          <span className="text-xs text-slate-500 shrink-0">🔗</span>
          <span className="text-sm text-slate-700 truncate" data-testid="host-referral-url">
            {data.referral_url}
          </span>
        </div>
        <Button
          onClick={copyLink}
          className="rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white px-6"
          data-testid="host-referral-copy"
        >
          {copied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy link</>}
        </Button>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-center">
          <Users className="w-4 h-4 text-slate-500 mx-auto" />
          <div className="mt-2 font-serif text-3xl text-[#0A1128]" data-testid="host-referral-signups">{data.signup_count}</div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-slate-500 mt-1">Sign-ups</div>
        </div>
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-center">
          <Check className="w-4 h-4 text-emerald-600 mx-auto" />
          <div className="mt-2 font-serif text-3xl text-emerald-700" data-testid="host-referral-certified">{data.certified_count}</div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-emerald-700 mt-1">Certified</div>
        </div>
        <div className="rounded-2xl bg-[#F9F3E5] border border-[#D4AF37]/40 p-4 text-center">
          <DollarSign className="w-4 h-4 text-[#D4AF37] mx-auto" />
          <div className="mt-2 font-serif text-3xl text-[#0A1128]" data-testid="host-referral-credit">
            ${data.credit_available_dollars ?? data.credit_dollars ?? 0}
          </div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-slate-500 mt-1">Available</div>
        </div>
      </div>

      {(data.credit_used_dollars ?? 0) > 0 && (
        <div
          className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-center gap-2 text-xs text-emerald-800"
          data-testid="host-referral-used-note"
        >
          <Check className="w-4 h-4 shrink-0" />
          <span>
            <b>${data.credit_used_dollars} redeemed</b> so far across past checkouts.
            <span className="text-emerald-700"> (Earned total: ${data.credit_earned_dollars ?? 0})</span>
          </span>
        </div>
      )}

      <p className="mt-5 text-[11px] text-slate-500 flex items-center gap-1.5" data-testid="host-referral-auto-note">
        <Sparkles className="w-3 h-3 text-emerald-600" />
        Credit is auto-applied to your next Stripe checkout via a one-time coupon — nothing manual required.
      </p>
    </div>
  );
}
